/**
 * ChatPromptText component for the Indigo design system.
 *
 * Right-aligned user prompt bubble used inside chat surfaces. Mirrors the
 * existing ChatbotModal user-message styling but extracted into a reusable
 * design-system primitive so consumers don't have to re-implement the
 * hover-popover with `Copy` / `Edit` actions.
 *
 * Behaviour:
 *  - Renders the prompt text inside a gray-100 bubble.
 *  - Shows a hover popover (timestamp + actions) on hover/focus of the
 *    bubble area only — hovering an attachment rendered below does not
 *    trigger it.
 *  - Optionally renders an `attachments` slot under the bubble with an
 *    8px gap (caller decides what to render — typically a list of
 *    `ChatPromptFile`s).
 *  - When `editing` is true, the bubble is replaced with an inline
 *    textarea + Cancel/Send buttons (controlled by the caller).
 *
 * Visual feedback for the copy action is owned by the caller via the
 * `copied` prop; the component does not manage that state internally so
 * callers can keep a single source of truth (matching ChatbotModal's
 * `copiedIndex` pattern).
 *
 * @example
 * ```tsx
 * <ChatPromptText
 *   text="Is Dr. Sandra Okafor available this week?"
 *   timestamp={msg.timestamp}
 *   copied={copiedIdx === idx}
 *   onCopy={() => handleCopy(msg.content, idx)}
 *   onEdit={() => handleEditStart(idx, msg.content)}
 * />
 * ```
 *
 * @example Edit mode
 * ```tsx
 * <ChatPromptText
 *   text={msg.content}
 *   editing={editingIndex === idx}
 *   editValue={editValue}
 *   onEditChange={setEditValue}
 *   onEditCancel={handleEditCancel}
 *   onEditSend={handleEditSend}
 * />
 * ```
 */
import { clsx } from "clsx";
import { forwardRef, KeyboardEvent, ReactNode, useEffect, useRef } from "react";
import { Button } from "../../Button/Button";
import { Icon } from "../../Icons";
import "./ChatPromptText.css";

export interface ChatPromptTextProps {
  /** Prompt text to render. */
  text: string;
  /**
   * Timestamp shown at the top of the hover popover. Accepts a `Date` or
   * a preformatted string. When omitted the popover renders without a
   * timestamp row.
   */
  timestamp?: Date | string;
  /** Optional slot rendered under the bubble (typically attachment chips). */
  attachments?: ReactNode;
  /**
   * When set, `text` is a truncated preview: a "See more" link renders
   * inline at its end and calls this to reveal the full message.
   */
  onShowMore?: () => void;
  /** Copy click handler. When omitted, the Copy action is hidden. */
  onCopy?: () => void;
  /** Edit click handler. When omitted, the Edit action is hidden. */
  onEdit?: () => void;
  /**
   * When `true`, the Copy action shows a "Copied" confirmation state.
   * Caller is responsible for resetting this flag.
   */
  copied?: boolean;
  /**
   * When `true`, the bubble is replaced with an inline textarea and
   * Cancel/Send action buttons. The caller fully controls the textarea
   * value via `editValue` / `onEditChange`.
   */
  editing?: boolean;
  /** Current value of the edit textarea. */
  editValue?: string;
  /** Called when the user types in the edit textarea. */
  onEditChange?: (value: string) => void;
  /** Called when the user clicks Cancel or presses `Escape`. */
  onEditCancel?: () => void;
  /**
   * Called when the user clicks Send or presses `Enter` (without Shift).
   * The Send button is disabled when `editValue` is empty/whitespace.
   */
  onEditSend?: () => void;
  /** Optional max length for the edit textarea. */
  editMaxLength?: number;
  /** Optional placeholder for the edit textarea. */
  editPlaceholder?: string;
  /** Additional CSS class applied to the root. */
  className?: string;
  /** Test id for the root element. */
  "data-testid"?: string;
}

function defaultFormatTimestamp(value: Date | string): string {
  if (typeof value === "string") return value;
  return value.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export const ChatPromptText = forwardRef<HTMLDivElement, ChatPromptTextProps>(
  (
    {
      text,
      timestamp,
      attachments,
      onShowMore,
      onCopy,
      onEdit,
      copied = false,
      editing = false,
      editValue = "",
      onEditChange,
      onEditCancel,
      onEditSend,
      editMaxLength,
      editPlaceholder,
      className,
      "data-testid": dataTestId,
    },
    ref,
  ) => {
    const showPopover = Boolean(timestamp || onCopy || onEdit);
    const formattedTimestamp = timestamp
      ? defaultFormatTimestamp(timestamp)
      : null;

    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
      if (editing) {
        const ta = textareaRef.current;
        if (ta) {
          ta.focus();
          // Place caret at the end so the user can append immediately.
          const len = ta.value.length;
          ta.setSelectionRange(len, len);
        }
      }
    }, [editing]);

    // Auto-size the textarea to its content.
    // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
    useEffect(() => {
      if (!editing) return;
      const ta = textareaRef.current;
      if (!ta) return;
      ta.style.height = "auto";
      ta.style.height = `${ta.scrollHeight}px`;
    }, [editing, editValue]);

    const handleEditKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        !event.nativeEvent.isComposing
      ) {
        event.preventDefault();
        if (editValue.trim()) onEditSend?.();
      } else if (event.key === "Escape") {
        event.preventDefault();
        onEditCancel?.();
      }
    };

    if (editing) {
      return (
        <div
          ref={ref}
          className={clsx(
            "indigo-chat-prompt-text",
            "indigo-chat-prompt-text--editing",
            className,
          )}
          data-testid={dataTestId}
        >
          <div className="indigo-chat-prompt-text__edit">
            <textarea
              ref={textareaRef}
              className="indigo-chat-prompt-text__edit-textarea"
              value={editValue}
              onChange={(event) => onEditChange?.(event.target.value)}
              onKeyDown={handleEditKeyDown}
              maxLength={editMaxLength}
              placeholder={editPlaceholder}
              rows={3}
            />
            <div className="indigo-chat-prompt-text__edit-actions">
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={onEditCancel}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="button"
                disabled={!editValue.trim()}
                onClick={onEditSend}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={clsx("indigo-chat-prompt-text", className)}
        data-testid={dataTestId}
      >
        <div className="indigo-chat-prompt-text__head">
          <div className="indigo-chat-prompt-text__bubble">
            {text}
            {onShowMore && (
              <button
                type="button"
                className="indigo-chat-prompt-text__more"
                onClick={onShowMore}
              >
                See more
              </button>
            )}
          </div>
          {showPopover && (
            <div
              className="indigo-chat-prompt-text__popover"
              role="menu"
              aria-label="Message actions"
            >
              {formattedTimestamp && (
                <div className="indigo-chat-prompt-text__timestamp">
                  {formattedTimestamp}
                </div>
              )}
              <div className="indigo-chat-prompt-text__actions">
                {onCopy && (
                  <button
                    type="button"
                    className="indigo-chat-prompt-text__action"
                    onClick={onCopy}
                    role="menuitem"
                  >
                    <Icon name={copied ? "check" : "copy"} size="xs" />
                    {copied ? "Copied" : "Copy"}
                  </button>
                )}
                {onEdit && (
                  <button
                    type="button"
                    className="indigo-chat-prompt-text__action"
                    onClick={onEdit}
                    role="menuitem"
                  >
                    <Icon name="pencil" size="xs" />
                    Edit
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        {attachments && (
          <div className="indigo-chat-prompt-text__attachments">
            {attachments}
          </div>
        )}
      </div>
    );
  },
);

ChatPromptText.displayName = "ChatPromptText";

export default ChatPromptText;
